import React, { useState } from "react";
import { HttpStatusCode } from "axios";

import { EMAIL_REGEX } from "../../consts/regex";

import { checkReferrerEmailExistence } from "../../services/authServices";

import useFullScreenLoading from "../useFullScreenLoading";
import { EmailVerification } from "../useEmailVerification";

import CommonInput from "../../components/Input/CommonInput";
import CommonButton from "../../components/Button";
import SimplePopup from "../../components/Popup/Simple";

type Props = { label?: string };

export default function useReferrerEmailVerification({
  label = "추천인 아이디(선택)",
}: Props): [
  { data: EmailVerification; isCompleted: boolean; validate: () => boolean },
  React.ReactNode
] {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [popup, setPopup] = useState<
    "emailNotFound" | "emailCompleted" | "emailNull" | ""
  >("");
  const [data, setData] = useState<EmailVerification>({
    email: "",
    duplicateChecked: false,
    authRequested: false,
    authCode: "",
  });
  const [isCompleted, setIsCompleted] = useState<boolean>(true);
  const [emailError, setEmailError] = useState("");
  const [autoCompleteList, setAutoCompleteList] = useState<string[]>([]);

  const emailInputRef = React.useRef<HTMLInputElement>(null);
  const loadingComponent = useFullScreenLoading({ isLoading });

  const hostList = [
    "gmail.com",
    "naver.com",
    "kakao.com",
    "apple.com",
    "nate.com",
    "hanmail.net",
    "daum.net",
  ];

  const emailValidate = (dryrun: boolean = false) => {
    if (data.email?.length > 0) {
      if (!EMAIL_REGEX.test(data.email as string)) {
        if (dryrun === false) {
          setEmailError("이메일 형식이 올바르지 않습니다.");
          emailInputRef.current?.focus();
        }
        return false;
      }
    }

    return true;
  };

  const validate = () => {
    if (!emailValidate()) return false;

    if (!isCompleted) {
      setEmailError("추천인 아이디 확인을 완료해주세요");
      emailInputRef.current?.focus();
      return false;
    }

    return true;
  };

  return [
    { data, isCompleted, validate },
    <div>
      {loadingComponent}
      <div className="py-2.5 text-sm text-brand-neutral-60">{label}</div>
      <div className="relative">
        <CommonInput
          type="email"
          placeholder="추천인 이메일을 입력해주세요"
          value={data.email ?? ""}
          onChange={(text) => {
            setData((prev) => ({ ...prev, email: text }));
            if (text.includes("@")) {
              const tempList = hostList.map(
                (host) => `${text.split("@")[0]}@${host}`
              );
              setAutoCompleteList(
                tempList.filter((item) => item.includes(text))
              );
            } else {
              setAutoCompleteList([]);
            }
            setEmailError("");
            setIsCompleted(text.length === 0); // 추천인 이메일란이 공란인 경우 true. 1자 이상 입력한 경우 false.
          }}
          className="focus:border-brand-primary-magenta"
          error={emailError}
          ref={emailInputRef}
          postfix={{
            content: (
              <CommonButton
                variant={
                  emailValidate(true) && !isCompleted ? "primary" : "tertiary-s"
                }
                onClick={async () => {
                  if (data.email.trim().length === 0) {
                    setPopup("emailNull");
                    setIsCompleted(true);
                    return;
                  }

                  if (!emailValidate()) return;

                  setEmailError("");
                  setIsLoading(true);
                  try {
                    const res = await checkReferrerEmailExistence({
                      email: data.email.trim() as string,
                    });

                    if (
                      res.status === HttpStatusCode.Ok &&
                      res.data.isActivated === false
                    ) {
                      setPopup("emailNotFound");
                      setIsCompleted(false);
                    } else {
                      setPopup("emailCompleted");
                      setIsCompleted(true);
                    }
                  } catch (e: any) {
                    setPopup("emailNotFound");
                  } finally {
                    setIsLoading(false);
                  }
                }}
              >
                조회
              </CommonButton>
            ),
            className: "pr-1",
          }}
        />
        {autoCompleteList.filter((item) => item !== data.email).length > 0 && (
          <div className="absolute top-[54px] py-[5px] z-20 bg-white border border-brand-primary-magenta w-full">
            {autoCompleteList
              .filter((item) => item !== data.email)
              .map((item, index) => {
                const [before, after] = item.split(data.email);
                return (
                  <div
                    key={index}
                    className="px-4 h-[40px] flex items-center"
                    onClick={() => {
                      setData((prev) => ({ ...prev, email: item }));
                      setAutoCompleteList([]);
                    }}
                  >
                    {before}
                    <strong>{data.email}</strong>
                    {after}
                  </div>
                );
              })}
          </div>
        )}
      </div>

      <SimplePopup
        isOpen={popup === "emailNotFound"}
        onClose={() => setPopup("")}
        message="존재하지 않는 이메일입니다."
      />

      <SimplePopup
        isOpen={popup === "emailCompleted"}
        onClose={() => setPopup("")}
        message="확인되었습니다."
      />

      <SimplePopup
        isOpen={popup === "emailNull"}
        onClose={() => setPopup("")}
        message="추천인 이메일을 입력해주세요."
      />
    </div>,
  ];
}
